import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import { Grid, Theme, Tooltip, Typography } from "@mui/material";
import { INewTemplateFlight } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { FlightChip } from "src/components/Flights/FlightChip";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { StatusIcon } from "src/pages/FlightBuilder/BuildStatus/StatusIcon";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";
import { milli2DaysHrsMin } from "src/utils";
import { EventTypes } from "../FlightBuilder";
import { validateFlight } from "./FlightRowEdit";

interface IFlightRowDisplayProps {
  flight: INewTemplateFlight;
  legIndex: number;
  mosaicPane: HTMLElement;
  onSubmit: (flightValues: INewTemplateFlight) => void;
  theme: Theme;
  eventType?: EventTypes;
  buildAsLocal: boolean;
  setEditRow: React.Dispatch<React.SetStateAction<number>>;
  startDate: number;
  setFocusField: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Show selected Flight Row in Edit Mode
 * @param props -
 */
export const FlightRowDisplay = (props: IFlightRowDisplayProps): JSX.Element => {
  const { flight, legIndex, mosaicPane, theme, eventType, buildAsLocal, setEditRow, startDate, setFocusField } = props;

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  const getTimezone = useCallback(
    (location: string) => {
      return airportOptions?.[location]?.timezoneId;
    },
    [airportOptions]
  );

  const originTZ = useMemo(
    () => (!!airportOptions && flight?.origin ? airportOptions[flight.origin]?.timezoneId : "Etc/UTC"),
    [airportOptions, flight.origin]
  );

  const destinationTZ = useMemo(
    () => (!!airportOptions && flight?.destination ? airportOptions[flight.destination]?.timezoneId : "Etc/UTC"),
    [airportOptions, flight.destination]
  );

  const getDateTimeMoment = useCallback(
    (time: string, location: string, dayOffset: number, departureMoment?: moment.Moment): moment.Moment | null => {
      if (!startDate || !time) {
        return null;
      }
      const offset = dayOffset ?? 0;
      const dateToCreate = moment.utc(startDate).add(offset, "days");
      const [year, month, date] = [dateToCreate.year(), dateToCreate.month(), dateToCreate.date()];
      const [hours, minutes] = time.split(":").map((val: string) => parseInt(val));
      const tz = !buildAsLocal || !location ? "Etc/UTC" : getTimezone(location);
      const dateObject = moment.tz([year, month, date, hours, minutes], tz);
      // if departure is after arrival, assume overnight flight and add a day.
      return departureMoment && departureMoment > dateObject ? dateObject.add(1, "days") : dateObject;
    },
    [getTimezone, startDate, buildAsLocal]
  );

  const isValid = Object.keys(validateFlight(flight)).length === 0;

  const departureMoment = useMemo(() => {
    return getDateTimeMoment(flight?.scheduledOut, flight?.origin, flight?.dayOffset);
  }, [flight?.dayOffset, flight?.origin, flight?.scheduledOut, getDateTimeMoment]);
  const departureIsDST = useMemo(() => departureMoment?.isDST && departureMoment?.isDST(), [departureMoment]);

  // arrival moment will adjust for DST offset as well as assign as overnight flight if applicable.
  const arrivalMoment = useMemo(() => {
    return getDateTimeMoment(flight?.scheduledIn, flight?.destination, flight?.dayOffset, departureMoment);
  }, [departureMoment, flight?.dayOffset, flight?.destination, flight?.scheduledIn, getDateTimeMoment]);
  const arrivalIsDST = useMemo(() => arrivalMoment?.isDST && arrivalMoment?.isDST(), [arrivalMoment]);

  const blockDuration = useMemo(() => {
    if (!departureMoment || !arrivalMoment) {
      return "d+HH:mm";
    }
    const duration = moment.duration(arrivalMoment.diff(departureMoment));
    return milli2DaysHrsMin(duration.asMilliseconds(), true);
  }, [departureMoment, arrivalMoment]);

  const flightTypeColor = flight?.flightType?.color ? `#${flight?.flightType?.color}` : theme.custom.forms.inputBack;
  const contrastText = theme.palette.getContrastText(flightTypeColor);

  const handleSetFocusField = useCallback(
    (field: string) => (event: React.MouseEvent) => {
      setFocusField(field);
      setEditRow(legIndex);
    },
    [setFocusField, setEditRow, legIndex]
  );

  const departureTooltip = buildAsLocal
    ? `${departureMoment?.format("MMM Do, Y HH:mm z")} ${departureIsDST ? "(in DST)" : ""}`
    : departureMoment?.utc()?.format("MMM Do, Y HH:mm z");

  const arrivalTooltip = buildAsLocal
    ? `${arrivalMoment?.format("MMM Do, Y HH:mm z")} ${arrivalIsDST ? "(in DST)" : ""}`
    : arrivalMoment?.utc()?.format("MMM Do, Y HH:mm z");

  return (
    <Grid item>
      <Grid className={"form_styles"}>
        <Grid container direction="row" className="flight_row">
          <Grid container direction="row" className="crucial_fields" flexWrap="nowrap">
            <Grid item className="checkbox">
              <CheckboxField
                checked={false}
                onChange={() => {
                  setFocusField(null);
                  setEditRow(legIndex);
                }}
              />
            </Grid>
            <Grid item className="flight_leg">
              <Typography variant="caption">{(legIndex + 1).toString()}</Typography>
            </Grid>
            <Grid
              container
              direction="row"
              className={`required_fields ${isValid ? "" : "invalid"}`}
              flexWrap="nowrap"
              alignItems="center"
            >
              {flight?._id?.includes("temp") ? (
                <Grid
                  item
                  onMouseDown={handleSetFocusField("flightNumber_input")}
                  sx={{
                    padding: "2px 8px",
                    margin: "5px",
                    borderRadius: "3px",
                    height: "24px",
                    width: "100px",
                    fontSize: "12px",
                    lineHeight: "20px",
                    backgroundColor: flightTypeColor,
                    color: contrastText,
                  }}
                >
                  {flight?.flightNumber ?? ""}
                </Grid>
              ) : (
                <Grid container sx={{ width: 330 }}>
                  <FlightChip data={flight} />
                  <div className="status_message">
                    <DoubleArrowIcon className="status_arrow" />
                    <StatusIcon className="status_icon" state="success" tooltipMessage="Build Successful" />
                  </div>
                </Grid>
              )}
              {flight?._id?.includes("temp") && (
                <>
                  <Tooltip
                    placement="top"
                    title={
                      departureMoment && originTZ
                        ? `UTC ${departureMoment.tz(originTZ).utcOffset()} minutes`
                        : "Time zone information unavailable"
                    }
                  >
                    <Grid
                      item
                      onMouseDown={handleSetFocusField("origin")}
                      sx={{
                        padding: "2px 8px",
                        margin: "5px",
                        height: "24px",
                        width: "100px",
                        fontSize: "12px",
                        lineHeight: "20px",
                        backgroundColor: theme.custom.forms.inputBack,
                        color: theme.custom.forms.inputText,
                      }}
                    >
                      {flight?.origin ?? ""}
                    </Grid>
                  </Tooltip>
                  <Tooltip
                    placement="top"
                    title={
                      arrivalMoment && destinationTZ
                        ? `UTC ${arrivalMoment.tz(destinationTZ).utcOffset()} minutes`
                        : "Time zone information unavailable"
                    }
                  >
                    <Grid
                      item
                      onMouseDown={handleSetFocusField("destination")}
                      sx={{
                        padding: "2px 8px",
                        margin: "5px",
                        height: "24px",
                        width: "100px",
                        fontSize: "12px",
                        lineHeight: "20px",
                        backgroundColor: theme.custom.forms.inputBack,
                        color: theme.custom.forms.inputText,
                      }}
                    >
                      {flight?.destination ?? ""}
                    </Grid>
                  </Tooltip>
                </>
              )}
              <Grid
                item
                onMouseDown={handleSetFocusField("flightType")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "100px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {flight?.flightType
                  ? `${flight?.flightType?.title ?? ""} - ${flight?.flightType?.description ?? ""}`
                  : ""}
              </Grid>
              <Grid
                item
                onMouseDown={handleSetFocusField("dayOffset_input")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "60px",
                  fontSize: "12px",
                  textAlign: "left",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {flight?.dayOffset}
              </Grid>
              <Tooltip placement="top" title={departureTooltip}>
                <Grid
                  item
                  onMouseDown={handleSetFocusField("scheduledOut_t_input")}
                  sx={{
                    padding: "2px 8px",
                    margin: "5px",
                    borderRadius: "0px",
                    height: "24px",
                    width: "70px",
                    fontSize: "12px",
                    textAlign: "center",
                    lineHeight: "20px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    backgroundColor: theme.custom.forms.inputBack,
                    color: !buildAsLocal ? theme.custom.forms.inputText : theme.palette.secondary.light,
                  }}
                >
                  {flight?.scheduledOut}
                </Grid>
              </Tooltip>
              <Tooltip placement="top" title={arrivalTooltip}>
                <Grid
                  item
                  onMouseDown={handleSetFocusField("scheduledIn_t_input")}
                  sx={{
                    padding: "2px 8px",
                    margin: "5px",
                    borderRadius: "0px",
                    height: "24px",
                    width: "70px",
                    fontSize: "12px",
                    textAlign: "center",
                    lineHeight: "20px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    backgroundColor: theme.custom.forms.inputBack,
                    color: !buildAsLocal ? theme.custom.forms.inputText : theme.palette.secondary.light,
                  }}
                >
                  {flight?.scheduledIn}
                </Grid>
              </Tooltip>
              <Typography className="block_duration" variant="caption">
                {blockDuration}
              </Typography>
            </Grid>

            <Grid container direction="row" className="optional_fields" flexWrap="nowrap">
              <Grid
                item
                onMouseDown={handleSetFocusField("aircraft")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px 5px 5px 10px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "auto",
                  minWidth: "140px",
                  maxWidth: "160px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {flight?.aircraft?.registration
                  ? `${flight?.aircraft.registration} (${
                      flight?.aircraft?.aircraftType && flight?.aircraft?.aircraftType?.title
                    })`
                  : ""}
              </Grid>
              <Grid
                item
                onMouseDown={handleSetFocusField("aircraftConfiguration")}
                sx={{
                  padding: "2px 8px",
                  margin: "5px 5px 5px 5px",
                  borderRadius: "0px",
                  height: "24px",
                  width: "100px",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: "20px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  backgroundColor: theme.custom.forms.inputBack,
                  color: theme.custom.forms.inputText,
                }}
              >
                {flight?.aircraftConfiguration?.title ? `${flight?.aircraftConfiguration?.title ?? ""}` : ""}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
